import React, { useCallback, useMemo } from 'react';
import type { FunctionComponent } from 'react';
import { Auth } from 'app/scripts/db/Auth';
import { CloseIcon } from '@trello/nachos/icons/close';
import { IconQuietColor } from '@trello/nachos/icon';
import { token } from '@trello/theme';
import { CloseButton } from 'app/src/components/CloseButton';
import { forTemplate } from '@trello/i18n';
import { currentLocale } from '@trello/locale';
import { idToDate } from '@trello/dates';
import { sendErrorEvent, ErrorBoundary } from '@trello/error-reporting';
import { inviteTokenForModel } from '@trello/invitation-tokens';
import { useInviteeOrientationQuery } from 'app/scripts/views/board/InviteeOrientationQuery.generated';
import { useInviteeOrientationBoardQuery } from './InviteeOrientationBoardQuery.generated';
import { useDismissInviteeOrientationMutation } from './DismissInviteeOrientationMutation.generated';
import styles from './InviteeOrientationView.less';
import cx from 'classnames';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';

const format = forTemplate('invitee_orientation');
const DISMISS_MESSAGE = 'invitee-orientation';

interface Props {
  boardId: string;
}

export const InviteeOrientationView: FunctionComponent<Props> = ({
  boardId,
}) => {
  const { data } = useInviteeOrientationQuery({
    variables: { memberId: 'me' },
    waitOn: ['MemberHeader'],
  });

  const { data: boardData } = useInviteeOrientationBoardQuery({
    variables: {
      boardId: boardId,
    },
    waitOn: ['None'],
  });
  const member = data?.member;
  const board = boardData?.board;
  const [dismissPopover] = useDismissInviteeOrientationMutation();
  const oneTimeMessagesDismissed = useMemo(
    () => member?.oneTimeMessagesDismissed ?? [],
    [member?.oneTimeMessagesDismissed],
  );

  const isDismissed = oneTimeMessagesDismissed.includes(DISMISS_MESSAGE);

  const onDismiss = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'invitedBoardInlineDialogDismissButton',
      source: getScreenFromUrl(),
    });
    try {
      await dismissPopover({
        variables: {
          memberId: 'me',
          messageId: DISMISS_MESSAGE,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          addOneTimeMessagesDismissed: {
            id: 'me',
            oneTimeMessagesDismissed: oneTimeMessagesDismissed!.concat([
              DISMISS_MESSAGE,
            ]),
            __typename: 'Member',
          },
        },
      });
    } catch (err) {
      // @ts-expect-error
      sendErrorEvent(err, {
        tags: {
          ownershipArea: 'trello-smoothie',
          feature: 'Invitee Orientation View',
        },
      });
    }
  }, [dismissPopover, oneTimeMessagesDismissed]);

  /**
   * You should see the popover if:
   * 1. You are logged in
   * 2. You haven't dismissed the popover before
   * 3. You have an invite token for this board
   * 4. Your account is newer than the threshold
   */

  const shouldSeePopover = () => {
    if (!board) {
      return;
    }
    if (
      Auth.isLoggedIn() &&
      !isDismissed &&
      inviteTokenForModel(boardId) &&
      idToDate(Auth.myId()) >= new Date('2019-06-15')
    ) {
      Analytics.sendScreenEvent({
        name: 'viewInvitedBoardInlineDialog',
      });
      return true;
    } else {
      return false;
    }
  };

  const membersLocaleLink = () => {
    let link;
    if (currentLocale === 'en-US') {
      link = 'https://trello.com/en/guide';
    } else {
      link = `https://trello.com/${currentLocale}/guide`;
    }
    return link;
  };

  const sendSeeGuideButtonEvent = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'seeGuideButton',
      source: getScreenFromUrl(),
    });
  }, []);

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-smoothie',
        feature: 'Invitee Orientation View',
      }}
    >
      {shouldSeePopover() ? (
        <div className={styles.inviteeOrientationWrapper}>
          <div
            className={cx(
              styles.inviteeOrientationMessageContainer,
              styles.arrow,
            )}
          >
            <div className={styles.inviteeOrientationHeader}>
              <h1>{format('welcome-to-trello-wave')}</h1>
              <CloseButton
                className={styles.close}
                onClick={onDismiss}
                closeIcon={
                  <CloseIcon
                    size="small"
                    color={token('color.icon.subtle', IconQuietColor)}
                  />
                }
              />
            </div>

            <div className={styles.inviteeOrientationText}>
              <p>{format('main-message')}</p>
              <li>{format('main-message-list1')}</li>
              <li>{format('main-message-list2')}</li>
              <li>{format('main-message-list3')}</li>
              <p className={styles.trelloBasics}>
                {format('main-trello-basics')}
              </p>
              <a
                className={cx(
                  'nch-button nch-button--primary .full',
                  styles.guideButton,
                )}
                target="_blank"
                href={membersLocaleLink()}
                onClick={sendSeeGuideButtonEvent}
              >
                {format('getting-started-button')}
              </a>
            </div>
          </div>

          <div
            className={cx(
              styles.inviteeOrientationAvatarContainer,
              styles.active,
            )}
          >
            <img
              alt=""
              role="presentation"
              className={styles.inviteeOrientationAvatar}
            ></img>
          </div>
        </div>
      ) : null}
    </ErrorBoundary>
  );
};
